
import { ExternalLink } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Candidate } from "./candidate-types";

interface CandidateTableProps {
  candidates: Candidate[];
}

export const CandidateTable = ({ candidates }: CandidateTableProps) => {
  const renderEmptyRows = () => {
    return Array(5).fill(null).map((_, index) => (
      <TableRow key={`empty-${index}`} className="transition-colors bg-secondary-light/5">
        <TableCell className="text-[12px] p-1 text-white/50">{index + 1}</TableCell>
        <TableCell className="text-[12px] p-1" />
        <TableCell className="text-[12px] p-1" />
        <TableCell className="text-[12px] p-1" />
        <TableCell className="text-[12px] p-1" />
        <TableCell className="text-[12px] p-1" />
        <TableCell className="text-[12px] p-1" />
      </TableRow>
    ));
  };

  return (
    <div className="h-[36vh] overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-primary/30 hover:bg-primary/30">
            <TableHead className="w-[3%] text-[11px] text-white font-semibold text-center py-1">SN</TableHead>
            <TableHead className="w-[15%] text-[11px] text-white font-semibold text-center py-1">Candidate</TableHead>
            <TableHead className="w-[3%] text-[11px] text-white font-semibold text-center py-1">Status</TableHead>
            <TableHead className="w-[42%] text-[11px] text-white font-semibold text-center py-1">Summary</TableHead>
            <TableHead className="w-[37%] text-[11px] text-white font-semibold text-center py-1">Differentiators</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {candidates.length > 0 ? (
            candidates.map((candidate, index) => (
              <TableRow key={candidate.id} className="hover:bg-primary/20 transition-colors bg-secondary-light/5">
                <TableCell className="text-[11px] p-0.5 text-center">
                  <span className="text-white">{index + 1}</span>
                </TableCell>
                <TableCell className="text-[11px] p-0.5 text-left">
                  <div className="font-medium text-white">
                    {candidate.candidate_name}
                  </div>
                </TableCell>
                <TableCell className="text-[11px] p-0.5">
                  <div className="space-y-1">
                    <span className={`px-1.5 py-0.5 rounded-full block text-center ${candidate.openToWork ? 'bg-green-500/20 text-white' : 'bg-red-500/20 text-white'}`}>
                      {candidate.openToWork ? 'Open to work' : 'Not open'}
                    </span>
                    <span className="text-white block text-center">
                      {candidate.matchPercentage}% match
                    </span>
                    {candidate.linkedinUrl && (
                      <a href={candidate.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-purple-500 hover:underline inline-flex items-center gap-1 justify-center w-full">
                        LinkedIn <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-[11px] p-0.5 text-left">
                  <div className="text-white">
                    {candidate.summary}
                  </div>
                </TableCell>
                <TableCell className="text-[11px] p-0.5 text-left">
                  <ul className="space-y-0.5">
                    {candidate.differentiators
                      .filter(diff => diff.trim() !== ':' && diff.trim().length > 0)
                      .map((diff, idx) => (
                        <li key={idx} className="text-white flex items-start gap-1">
                          <span className="text-white min-w-[8px]">•</span>
                          <span className="text-left">{diff}</span>
                        </li>
                      ))}
                  </ul>
                </TableCell>
              </TableRow>
            ))
          ) : (
            renderEmptyRows()
          )}
        </TableBody>
      </Table>
    </div>
  );
};
